import { errorMessage } from '../../lib/errors';
import { Dialog } from '../Dialog';
import { useId, useMemo, useRef, useState } from 'react';
import type { AIUsage, AIUsageEvent } from '../../hooks/useAIUsage';
import { launchProCheckout } from '../../lib/billing';

interface AIUsageCardProps {
  usage: AIUsage;
}

interface TimelinePoint {
  date: Date;
  balance: number;
  percentage: number;
  event?: AIUsageEvent;
}

function formatCreditCount(credits: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: credits >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(credits);
}

function periodBounds(period: string): { start: Date; end: Date } {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) {
    const now = new Date();
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 1, 1) };
  }
  return {
    start: new Date(Date.UTC(year, month - 1, 1)),
    end: new Date(Date.UTC(year, month, 1)),
  };
}

function buildTimeline(usage: AIUsage): TimelinePoint[] {
  const allowance = Math.max(usage.credit_allowance, 0);
  const { start, end } = periodBounds(usage.period);
  const events = [...(usage.usage_events ?? [])]
    .filter(event => event.credits_used > 0)
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  let balance = allowance;
  const points: TimelinePoint[] = [{ date: start, balance, percentage: allowance > 0 ? 100 : 0 }];

  events.forEach(event => {
    balance = Math.max(0, balance - event.credits_used);
    points.push({
      date: new Date(event.occurred_at),
      balance,
      percentage: allowance > 0 ? (balance / allowance) * 100 : 0,
      event,
    });
  });

  // Older API responses have only an aggregate. Retain the correct visual even
  // while a mixed-version deployment is rolling out.
  const unrepresented = Math.max(0, usage.credits_used - events.reduce((sum, event) => sum + event.credits_used, 0));
  if (unrepresented > 0) {
    balance = Math.max(0, balance - unrepresented);
    points.push({ date: new Date(), balance, percentage: allowance > 0 ? (balance / allowance) * 100 : 0 });
  }

  const now = new Date();
  points.push({
    date: now < start ? start : now > end ? end : now,
    balance,
    percentage: allowance > 0 ? (balance / allowance) * 100 : 0,
  });
  return points;
}

function stepPath(points: TimelinePoint[], width: number, height: number, inset = 8): string {
  const { start, end } = periodBounds(points[0]?.date.toISOString().slice(0, 7) ?? '');
  const duration = Math.max(1, end.getTime() - start.getTime());
  const x = (date: Date): number => inset + Math.min(1, Math.max(0, (date.getTime() - start.getTime()) / duration)) * (width - inset * 2);
  const y = (percentage: number): number => inset + (1 - Math.min(100, Math.max(0, percentage)) / 100) * (height - inset * 2);
  if (points.length === 0) return '';
  let path = `M ${x(points[0]!.date)} ${y(points[0]!.percentage)}`;
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index]!;
    path += ` H ${x(point.date)} V ${y(point.percentage)}`;
  }
  return path;
}

function CreditGraph({ usage, large = false }: { usage: AIUsage; large?: boolean }): import("react").JSX.Element {
  const points = useMemo(() => buildTimeline(usage), [usage]);
  const width = large ? 760 : 190;
  const height = large ? 210 : 46;
  const path = stepPath(points, width, height, large ? 24 : 5);
  const areaPath = `${path} V ${height - (large ? 24 : 5)} H ${large ? 24 : 5} Z`;

  return (
    <svg
      className={large ? 'ai-usage-graph large' : 'ai-usage-graph'}
      viewBox={`0 0 ${width} ${height}`}
      role={large ? 'img' : undefined}
      aria-label={large ? `AI credit balance over ${usage.period}` : undefined}
      aria-hidden={large ? undefined : true}
      preserveAspectRatio="none"
    >
      {large && <>
        <line x1="24" y1="24" x2="736" y2="24" className="ai-usage-gridline" />
        <line x1="24" y1="105" x2="736" y2="105" className="ai-usage-gridline" />
        <line x1="24" y1="186" x2="736" y2="186" className="ai-usage-gridline" />
      </>}
      <path d={areaPath} className="ai-usage-area" />
      <path d={path} className="ai-usage-line" />
      {large && points.filter(point => point.event).map(point => {
        const { start, end } = periodBounds(usage.period);
        const x = 24 + Math.min(1, Math.max(0, (point.date.getTime() - start.getTime()) / Math.max(1, end.getTime() - start.getTime()))) * 712;
        const y = 24 + (1 - Math.min(100, Math.max(0, point.percentage)) / 100) * 162;
        return <circle key={point.event!.task_id} cx={x} cy={y} r="4" className="ai-usage-point" />;
      })}
    </svg>
  );
}

function AIUsageModal({ usage, onClose }: { usage: AIUsage; onClose: () => void }): import("react").JSX.Element {
  const headingId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const timeline = useMemo(() => buildTimeline(usage), [usage]);
  const eventPoints = timeline.filter((point): point is TimelinePoint & { event: AIUsageEvent } => Boolean(point.event));
  const { start, end } = periodBounds(usage.period);
  const periodLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(start);

  return (
    <Dialog label="Monthly credit usage" onClose={onClose}>
      <section className="ai-usage-modal" role="dialog" aria-modal="true" aria-labelledby={headingId}>
        <header className="ai-usage-modal-header">
          <div>
            <span>AI CREDITS</span>
            <h2 id={headingId}>Monthly credit usage</h2>
            <p>{periodLabel} · Credits reset at the start of each billing month.</p>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close credit usage details">×</button>
        </header>

        <div className="ai-usage-summary">
          <div><span>Starting credits</span><strong>{usage.credit_allowance.toLocaleString()}</strong></div>
          <div><span>Credits used</span><strong>{usage.credits_used.toLocaleString()}</strong></div>
          <div><span>Credits remaining</span><strong>{(usage.credits_remaining ?? 0).toLocaleString()}</strong></div>
        </div>

        <div className="ai-usage-chart-panel">
          <div className="ai-usage-chart-labels"><span>100%</span><span>50%</span><span>0%</span></div>
          <CreditGraph usage={usage} large />
          <div className="ai-usage-chart-dates">
            <span>{start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
            <span>{new Date(end.getTime() - 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
          </div>
        </div>

        <div className="ai-usage-table-wrap">
          <table className="ai-usage-table">
            <caption>Credit usage by task</caption>
            <thead><tr><th>When</th><th>Task</th><th>Credits used</th><th>Balance</th></tr></thead>
            <tbody>
              {eventPoints.length === 0 ? (
                <tr><td colSpan={4} className="ai-usage-empty">No credits used in this period.</td></tr>
              ) : eventPoints.map(point => (
                <tr key={point.event.task_id}>
                  <td>{new Date(point.event.occurred_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                  <td><strong>{point.event.task_name}</strong><small>{point.event.task_id}</small></td>
                  <td className="ai-usage-number">−{point.event.credits_used.toLocaleString()}</td>
                  <td className="ai-usage-number">{point.balance.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Dialog>
  );
}

export function AIUsageCard({ usage }: AIUsageCardProps): import("react").JSX.Element {
  const [open, setOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [upgradedPlan, setUpgradedPlan] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const plan = (upgradedPlan ?? usage.plan ?? 'free').toLocaleLowerCase();
  const planLabel = plan ? `${plan.charAt(0).toLocaleUpperCase()}${plan.slice(1)}` : 'Free';
  const remainingPercentage = usage.credit_allowance > 0
    ? Math.max(0, Math.min(100, ((usage.credits_remaining ?? 0) / usage.credit_allowance) * 100))
    : 0;
  const closeModal = (): void => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const upgrade = async (event: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
    event.preventDefault();
    if (upgrading) return;
    setUpgrading(true);
    setUpgradeError(null);
    try {
      const billing = await launchProCheckout();
      if (billing) setUpgradedPlan(billing.plan ?? 'pro');
    } catch (error) {
      setUpgradeError(error instanceof Error ? errorMessage(error) : 'Unable to open secure checkout.');
    } finally {
      setUpgrading(false);
    }
  };

  const planBanner = (
    <div className={`tenant-plan-banner${plan === 'free' ? ' free' : ''}`} role="status" aria-label={`Tenant plan: ${planLabel}`}>
      <span className="tenant-plan-copy"><small>Tenant plan</small><strong>{planLabel}</strong></span>
      {plan === 'free' && (
        <a href="#upgrade-plan" onClick={event => void upgrade(event)} aria-disabled={upgrading}>
          {upgrading ? 'Opening checkout…' : 'Upgrade'}
        </a>
      )}
    </div>
  );

  if (plan === 'free') {
    return (
      <div className="tenant-plan-and-usage">
        {planBanner}
        {upgradeError && <div className="tenant-plan-error" role="alert">{upgradeError}</div>}
      </div>
    );
  }

  if (!usage.enabled || usage.unlimited) {
    return (
      <div className="tenant-plan-and-usage">
        {planBanner}
      <div className={`app-sidebar-token-usage${!usage.enabled ? ' disabled' : ''}`} role="status">
        <span className="app-sidebar-token-icon" aria-hidden="true">✦</span>
        <span className="app-sidebar-token-copy">
          <small>AI credits</small>
          <strong>{usage.unlimited ? 'Unlimited' : 'Not enabled'}</strong>
        </span>
      </div>
        {upgradeError && <div className="tenant-plan-error" role="alert">{upgradeError}</div>}
      </div>
    );
  }

  return <>
    <div className="tenant-plan-and-usage">
      {planBanner}
    <button
      ref={triggerRef}
      type="button"
      className="app-sidebar-token-usage ai-usage-trigger"
      onClick={() => setOpen(true)}
      aria-label={`${formatCreditCount(usage.credits_remaining ?? 0)} AI credits remaining, ${remainingPercentage.toFixed(0)} percent. View usage details`}
      title="View detailed AI credit usage"
    >
      <span className="ai-usage-trigger-heading"><small>AI credits</small><strong>{remainingPercentage.toFixed(0)}%</strong></span>
      <CreditGraph usage={usage} />
      <span className="ai-usage-trigger-footer"><strong>{formatCreditCount(usage.credits_remaining ?? 0)} remaining</strong></span>
    </button>
      {upgradeError && <div className="tenant-plan-error" role="alert">{upgradeError}</div>}
    {open && <AIUsageModal usage={usage} onClose={closeModal} />}
    </div>
  </>;
}
